import React from 'react';
import type { AdminBookingResponseDto } from '@pro-sto/api-client';
import { UserIcon } from '@pro-sto/ui';
import { formatPrice } from '@pro-sto/shared/utils';
import { formatClientName } from '@/features/clients';
import logoDotsIcon from '@/assets/logo-dots.svg';
import { getDayCardSize, parseCssSize } from '../CalendarGrid/calendarGeometry';
import './BookingCard.css';

export interface BookingCardProps {
  booking: AdminBookingResponseDto;
  onClick: () => void;
  style?: React.CSSProperties;
  variant?: 'day' | 'week';
  isInvalid?: boolean;
  isDragging?: boolean;
  onMovePointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onResizeStartPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
  onResizeEndPointerDown?: (event: React.PointerEvent<HTMLDivElement>) => void;
}

const STATUS_LABELS: Record<string, string> = {
  completed: 'Выполнен',
  confirmed: 'Подтверждена',
  pending_confirmation: 'Ожидает',
  cancelled: 'Отменён',
  paid: 'Оплачена',
};

const formatTime = (time: string): string => {
  const date = new Date(time);
  return date.toLocaleTimeString('ru-RU', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

const isUnknownCarField = (value?: string | null): boolean =>
  !value || value.trim().toLowerCase() === 'не указана';

const formatCarTitle = (booking: AdminBookingResponseDto): string => {
  const car = booking.car;
  if (!car) return 'Авто не указано';

  const parts = [car.make, car.model].filter((part) => !isUnknownCarField(part));
  return parts.join(' ') || 'Авто не указано';
};

const BookingCard: React.FC<BookingCardProps> = ({
  booking,
  onClick,
  style,
  variant = 'week',
  isInvalid = false,
  isDragging = false,
  onMovePointerDown,
  onResizeStartPointerDown,
  onResizeEndPointerDown,
}) => {
  const startTime = formatTime(booking.start_time);
  const endTime = formatTime(booking.end_time);
  const displayStatus = booking.payment_status === 'paid' ? 'paid' : booking.status;
  const statusLabel = STATUS_LABELS[displayStatus];
  const carTitle = formatCarTitle(booking);
  const licensePlate = booking.car?.license_plate;
  const clientName = formatClientName(booking.client?.name) || 'Клиент';
  const additionalCount = booking.additionalServices?.length ?? 0;
  const price = booking.total_cost ?? booking.service?.price ?? 0;
  const serviceLabel =
    additionalCount > 0 ? `${booking.service.name} +${additionalCount}` : booking.service.name;
  const priceLabel = `${formatPrice(price)}₽`;
  const isFromApp = booking.created_by_source === 'client_app';
  const isDay = variant === 'day';
  const daySize = isDay ? getDayCardSize(parseCssSize(style?.height)) : 'full';
  const compactSummary = [
    licensePlate ? `${carTitle} ${licensePlate}` : carTitle,
    serviceLabel,
    priceLabel,
    clientName,
    startTime,
  ]
    .filter(Boolean)
    .join(' · ');

  const handleClick = () => {
    onClick();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const handleMovePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (event.button !== 0 || !onMovePointerDown) return;
    onMovePointerDown(event);
  };

  const handleResizeStartPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onResizeStartPointerDown?.(event);
  };

  const handleResizeEndPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    event.stopPropagation();
    event.preventDefault();
    onResizeEndPointerDown?.(event);
  };

  const className = [
    'booking-card',
    isDay ? 'booking-card_day' : 'booking-card_week',
    isDay && daySize !== 'full' ? `booking-card_day_${daySize}` : '',
    `booking-card_status_${displayStatus}`,
    isInvalid ? 'booking-card_invalid' : '',
    isDragging ? 'booking-card_dragging' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const postNumber = booking.post?.orderNumber;
  const weekTitle = carTitle;
  const dayTitle = [carTitle, booking.service.name, `${startTime}–${endTime}`, clientName]
    .filter(Boolean)
    .join('\n');

  return (
    <div
      className={className}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      onPointerDown={isDay ? handleMovePointerDown : undefined}
      role="button"
      tabIndex={0}
      style={style}
      title={isDay ? dayTitle : weekTitle}
      aria-label={
        isDay
          ? `Заказ ${carTitle}, ${booking.service.name}, ${startTime}`
          : `Заказ ${booking.service.name} с ${startTime} до ${endTime}, статус: ${statusLabel ?? displayStatus}`
      }
      data-testid={`booking-card-${booking.uuid}`}
    >
      {isDay ? (
        <>
          <div className="booking-card__content">
            {daySize === 'mini' || daySize === 'short' ? (
              <p className="booking-card__summary" data-testid="booking-card-summary">
                {compactSummary}
              </p>
            ) : (
              <>
                <p className="booking-card__title-row">
                  <span className="booking-card__title" data-testid="booking-car-title">
                    {carTitle}
                  </span>
                </p>
                <p className="booking-card__service-row">
                  <span className="booking-card__service" data-testid="booking-service-name">
                    {serviceLabel}
                  </span>
                  <span className="booking-card__price" data-testid="booking-price">
                    {priceLabel}
                  </span>
                </p>
                <p className="booking-card__meta" data-testid="booking-executor">
                  <span className="booking-card__user-icon" aria-hidden="true">
                    <UserIcon />
                  </span>
                  <span className="booking-card__executor">{clientName}</span>
                  <span className="booking-card__dot" aria-hidden="true">·</span>
                  <span className="booking-card__time" data-testid="booking-time">
                    {startTime}
                  </span>
                </p>
              </>
            )}
          </div>
          {isFromApp && (
            <span
              className="booking-card__app-icon"
              aria-label="Заказ из приложения"
              data-testid="booking-app-source"
            >
              <img src={logoDotsIcon} alt="" width={14} height={12} />
            </span>
          )}
          {onResizeStartPointerDown && daySize !== 'mini' && (
            <div
              className="booking-card__resize-handle booking-card__resize-handle_start"
              onPointerDown={handleResizeStartPointerDown}
              data-testid={`booking-resize-start-${booking.uuid}`}
              aria-label="Изменить время начала"
            />
          )}
          {onResizeEndPointerDown && (
            <div
              className="booking-card__resize-handle booking-card__resize-handle_end"
              onPointerDown={handleResizeEndPointerDown}
              data-testid={`booking-resize-end-${booking.uuid}`}
              aria-label="Изменить время окончания"
            />
          )}
        </>
      ) : (
        <div className="booking-card__content">
          <p className="booking-card__title-row">
            <span className="booking-card__title" data-testid="booking-car-title">
              {carTitle}
            </span>
          </p>
          <p className="booking-card__meta" data-testid="booking-executor">
            {postNumber !== undefined && (
              <span className="booking-card__post">П{postNumber}</span>
            )}
            <span className="booking-card__time" data-testid="booking-time">
              {startTime}–{endTime}
            </span>
          </p>
        </div>
      )}
    </div>
  );
};

export default BookingCard;
